import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as SearchIcon } from "../../assets/search.svg";
import he from "he";
import style from "./style.module.scss";
import useDebounce from "./useDebounce";
import clsx from "clsx";

const fetchSuggestions = async (query) => {
  try {
    const response = await fetch(`/wp-json/sbm/v1/suggestions?q=${query}`);
    const data = await response.json();
    return {
      products: data.products || [],
      posts: data.posts || [],
    };
  } catch (error) {
    console.error("Error fetching suggestions:", error);
    return { products: [], posts: [] };
  }
};

const decodeHtmlEntities = (text) => {
  if (!text || typeof text !== "string") return "";
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

function SearchBar({ searchPageLink, hasTopCategoryMenu, searchOpen }) {
  // React i18next
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState({ products: [], posts: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("products");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedSearch = useDebounce(search, 500);
  const inputRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      setIsLoading(true);
      setShowSuggestions(true);
      fetchSuggestions(debouncedSearch).then((results) => {
        setSuggestions(results);
        setIsLoading(false);
      });
    } else {
      setSuggestions({ products: [], posts: [] });
      if (debouncedSearch.length < 3) {
        setShowSuggestions(false);
      }
    }
  }, [debouncedSearch]);

  // Fonction pour gérer la touche Escape
  const handleEscape = () => {
    setShowSuggestions(false);
    // Retirer le focus de l'input
    inputRef.current?.blur();
  };

  // Gestionnaire pour les clics extérieurs
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }

    // Ajouter l'écouteur d'événement pour les clics
    document.addEventListener("mousedown", handleClickOutside);

    // Ajouter l'écouteur d'événement pour la touche Escape
    function handleEscapeKey(event) {
      if (event.key === "Escape") {
        handleEscape();
      }
    }

    document.addEventListener("keydown", handleEscapeKey);

    // Nettoyer les écouteurs d'événements
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const handleKeyDown = (e) => {
    const currentSuggestions =
      activeTab === "products" ? suggestions.products : suggestions.posts;

    if (e.key === "Escape") {
      e.preventDefault(); // Empêcher le comportement par défaut
      handleEscape();
    } else if (e.key === "ArrowDown") {
      setSelectedSuggestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, currentSuggestions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      if (selectedSuggestionIndex === 0) {
        setSelectedSuggestionIndex(-1);
        setTimeout(() => {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(
            inputRef.current.value.length,
            inputRef.current.value.length
          );
        }, 0);
      } else {
        setSelectedSuggestionIndex((prevIndex) => Math.max(prevIndex - 1, -1));
      }
    } else if (e.key === "Enter") {
      if (selectedSuggestionIndex >= 0 && currentSuggestions.length > 0) {
        e.preventDefault();
        window.location.href = currentSuggestions[selectedSuggestionIndex].url;
      }
    }
  };

  const handleInputFocus = () => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      setShowSuggestions(true);
    }
  };

  useEffect(() => {
    if (searchOpen) setShowSuggestions(true);
  }, [searchOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (search && searchPageLink) {
      let params = new URLSearchParams({ q: search }).toString();
      window.location.href = `${searchPageLink}?${params}`;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    window.location.href = suggestion.url;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedSuggestionIndex(-1);
  };

  const currentSuggestions =
    activeTab === "products" ? suggestions.products : suggestions.posts;
  const hasSuggestions =
    suggestions.products.length > 0 || suggestions.posts.length > 0;

  // Determine if we should show results or the no results message
  const shouldShowResults = debouncedSearch && debouncedSearch.length >= 3;
  const shouldDisplaySuggestions =
    showSuggestions && (isLoading || shouldShowResults);

  return (
    <div className={style.block} ref={searchContainerRef}>
      <form className={style.searchForm} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t("Search for an item or a product")}…`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelectedSuggestionIndex(-1);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          aria-label={`${t("Search for an item or a product")}`}
        />
        <button className={style.searchBtn} type="submit">
          <SearchIcon />
        </button>
      </form>
      {shouldDisplaySuggestions && (
        <div className={style.suggestions}>
          {isLoading ? (
            <div className={style.loader}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 38 38"
                stroke="#000"
              >
                <g fill="none" fillRule="evenodd">
                  <g transform="translate(1 1)" strokeWidth="2">
                    <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
                    <path d="M36 18c0-9.94-8.06-18-18-18">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 18 18"
                        to="360 18 18"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </g>
                </g>
              </svg>
            </div>
          ) : hasSuggestions ? (
            <>
              <div className={style.tabsContainer}>
                <div className={style.tabsContainerBorderBottom} />
                <div className={style.tabs}>
                  <button
                    className={`${style.tabBtn} ${
                      activeTab === "products" ? style.active : ""
                    }`}
                    onClick={() => handleTabChange("products")}
                  >
                    {t("Products")}
                  </button>
                  <button
                    className={`${style.tabBtn} ${
                      activeTab === "posts" ? style.active : ""
                    }`}
                    onClick={() => handleTabChange("posts")}
                  >
                    {t("Articles")}
                  </button>
                </div>
              </div>
              <div
                className={clsx(
                  style.tabContent,
                  hasTopCategoryMenu && style.withTopCategoryMenu
                )}
              >
                {currentSuggestions.length === 0 ? (
                  <div className={style.noResults}>{t("No results found")}</div>
                ) : (
                  currentSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.id}
                      tabIndex={0}
                      className={`${style.suggestionItem} ${
                        index === selectedSuggestionIndex ? style.selected : ""
                      }`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSuggestionClick(suggestion);
                        }
                      }}
                      aria-label={`${
                        suggestion.brand ? suggestion.brand + " - " : ""
                      }${he.decode(suggestion.title)}`}
                    >
                      <div className={style.suggestionImage}>
                        {suggestion.image && suggestion.image.url ? (
                          <img
                            src={suggestion.image.url}
                            alt={he.decode(suggestion.image.alt) || ""}
                          />
                        ) : (
                          <div className={style.noImage}></div>
                        )}
                      </div>
                      <div className={style.suggestionContent}>
                        {suggestion.brand && (
                          <div className={style.suggestionBrand}>
                            {decodeHtmlEntities(suggestion.brand)}
                          </div>
                        )}
                        <div className={style.suggestionTitle}>
                          {decodeHtmlEntities(suggestion.title)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className={style.noResultsContainer}>
              <div className={style.noResultsTitle}>{t("No results")}</div>
              <div className={style.noResultsMessage}>
                {t("Sorry, we didn't find anything for")} «
                <strong>{search}</strong>».
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
